<script lang="ts">
  // The timeline filter bar — same design language as the members filter: a search
  // field + a boolean toggle on top, then labelled facet rows of soft pills that
  // fill with ink when selected. When (date range) and Sort are single-select
  // pills; Groups are multi-select. Owns presentation only — every value is a
  // $bindable the parent page holds, and any change calls `onchange` so the page
  // reloads + mirrors state to the URL.
  import MemberSelect from './MemberSelect.svelte';
  import type { LabelRow, PostSort, RangePreset } from '../admin';

  let {
    skoolId,
    labels,
    q = $bindable(''),
    authorId = $bindable<number | null>(null),
    groups = $bindable<string[]>([]),
    preset = $bindable<RangePreset>('all'),
    since = $bindable<string | null>(null),
    until = $bindable<string | null>(null),
    sort = $bindable<PostSort>('new'),
    unanswered = $bindable(false),
    matched,
    onchange,
  }: {
    skoolId: number | null | undefined;
    labels: LabelRow[];
    q?: string;
    authorId?: number | null;
    groups?: string[];
    preset?: RangePreset;
    /** Bare `YYYY-MM-DD` days, both inclusive — what the two date inputs and the
     *  range chip show. NOT instants: `<input type="date">` renders empty when
     *  handed anything else. The page owns the instants these display and resolves
     *  an edit back into one (see `readDayInputs` in AdminCommunityTimeline). */
    since?: string | null;
    until?: string | null;
    sort?: PostSort;
    unanswered?: boolean;
    matched: number;
    onchange: () => void;
  } = $props();

  const RANGES: { key: RangePreset; label: string }[] = [
    { key: '3d', label: '3d' },
    { key: '7d', label: '7d' },
    { key: '30d', label: '30d' },
    { key: '90d', label: '90d' },
    { key: 'all', label: 'All' },
  ];
  const SORTS: { key: PostSort; label: string }[] = [
    { key: 'new', label: 'Newest' },
    { key: 'comments', label: 'Most comments' },
    { key: 'upvotes', label: 'Most upvotes' },
  ];

  // Only groups the community actually uses, named, busiest first.
  const groupOptions = $derived(
    labels
      .filter((l) => l.name && l.posts > 0)
      .slice()
      .sort((a, b) => b.posts - a.posts),
  );

  let customOpen = $state(false);
  let searchTimer: ReturnType<typeof setTimeout> | undefined;

  const activeCount = $derived(
    (q.trim() ? 1 : 0) +
      (authorId != null ? 1 : 0) +
      groups.length +
      (preset !== 'all' ? 1 : 0) +
      (sort !== 'new' ? 1 : 0) +
      (unanswered ? 1 : 0),
  );

  // Shared pill styling — matches the members filter (ink fill when selected).
  const PILL = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors';
  function pillCls(on: boolean): string {
    return `${PILL} ${on ? 'bg-primary text-primary-content' : 'bg-base-200/70 text-base-content/70 hover:bg-base-200 hover:text-base-content'}`;
  }

  function onSearch(e: Event): void {
    q = (e.target as HTMLInputElement).value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(onchange, 300);
  }
  function pickRange(key: RangePreset): void {
    preset = key;
    if (key !== 'custom') {
      since = null;
      until = null;
      customOpen = false;
    }
    onchange();
  }
  function applyCustom(): void {
    if (!since && !until) return;
    preset = 'custom';
    customOpen = false;
    onchange();
  }
  function toggleGroup(id: string): void {
    groups = groups.includes(id) ? groups.filter((g) => g !== id) : [...groups, id];
    onchange();
  }
  function pickSort(key: PostSort): void {
    sort = key;
    onchange();
  }
  function toggleUnanswered(e: Event): void {
    unanswered = (e.target as HTMLInputElement).checked;
    onchange();
  }
  function onAuthor(): void {
    onchange();
  }
  function clearAll(): void {
    q = '';
    authorId = null;
    groups = [];
    preset = 'all';
    since = null;
    until = null;
    sort = 'new';
    unanswered = false;
    customOpen = false;
    onchange();
  }

  const customLabel = $derived(preset === 'custom' ? `${since ?? '…'} → ${until ?? 'now'}` : 'Custom');
</script>

<div class="rounded-2xl border border-base-300 bg-base-100 p-3 shadow-sm sm:p-4">
  <!-- search + author + unanswered -->
  <div class="flex flex-wrap items-center gap-3">
    <label class="flex h-9 min-w-[14rem] flex-1 items-center gap-2 rounded-xl border border-base-300 bg-base-100 px-3 focus-within:border-primary">
      <svg class="h-4 w-4 shrink-0 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
      <input
        type="search"
        placeholder="Search posts…"
        class="w-full bg-transparent text-sm outline-none placeholder:text-base-content/62"
        value={q}
        oninput={onSearch}
        aria-label="Search posts"
      />
    </label>
    <MemberSelect {skoolId} bind:value={authorId} onchange={onAuthor} placeholder="Any author" />
    <label class="flex cursor-pointer select-none items-center gap-2 text-xs text-base-content/72">
      <input type="checkbox" class="toggle toggle-sm" checked={unanswered} onchange={toggleUnanswered} />
      Unanswered only
    </label>
  </div>

  <!-- facets -->
  <div class="mt-3 space-y-2.5">
    <!-- When (single-select) -->
    <div class="flex items-start gap-3">
      <span class="eyebrow mt-1.5 w-24 shrink-0">When</span>
      <div class="flex flex-wrap items-center gap-1.5">
        {#each RANGES as r (r.key)}
          <button type="button" class={pillCls(preset === r.key)} onclick={() => pickRange(r.key)}>{r.label}</button>
        {/each}
        <div class="relative">
          <button type="button" class={pillCls(preset === 'custom')} onclick={() => (customOpen = !customOpen)}>
            <svg class="h-3.5 w-3.5 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18M8 2v4M16 2v4" /></svg>
            {customLabel}
          </button>
          {#if customOpen}
            <div class="absolute left-0 z-30 mt-1 w-64 rounded-xl border border-base-300 bg-base-100 p-3 shadow-xl">
              <div class="flex flex-col gap-2">
                <label class="flex items-center justify-between gap-2 text-xs text-base-content/72">
                  From
                  <input type="date" class="input input-xs input-bordered bg-base-100" bind:value={since} />
                </label>
                <label class="flex items-center justify-between gap-2 text-xs text-base-content/72">
                  To
                  <input type="date" class="input input-xs input-bordered bg-base-100" bind:value={until} />
                </label>
                <button type="button" class="btn btn-primary btn-xs mt-1" onclick={applyCustom}>Apply range</button>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Sort (single-select) -->
    <div class="flex items-start gap-3">
      <span class="eyebrow mt-1.5 w-24 shrink-0">Sort</span>
      <div class="flex flex-wrap gap-1.5">
        {#each SORTS as s (s.key)}
          <button type="button" class={pillCls(sort === s.key)} onclick={() => pickSort(s.key)}>{s.label}</button>
        {/each}
      </div>
    </div>

    <!-- Groups (multi-select) -->
    {#if groupOptions.length}
      <div class="flex items-start gap-3">
        <span class="eyebrow mt-1.5 w-24 shrink-0">Groups</span>
        <div class="flex flex-wrap gap-1.5">
          {#each groupOptions as l (l.label_id)}
            {@const on = groups.includes(l.label_id)}
            <button type="button" class={pillCls(on)} aria-pressed={on} onclick={() => toggleGroup(l.label_id)}>
              <!-- privacy-ok: a group/label filter chip — a product taxonomy, not a person -->
              {l.name}
              <span class="font-mono opacity-60">{l.posts}</span>
            </button>
          {/each}
        </div>
      </div>
    {/if}
  </div>

  <!-- footer: match count + clear (only when a filter is on) -->
  {#if activeCount > 0}
    <div class="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-base-200 pt-2.5 text-xs">
      <span class="tabular-nums text-base-content/68">
        {matched.toLocaleString()} post{matched === 1 ? '' : 's'} · {activeCount} filter{activeCount === 1 ? '' : 's'} active
      </span>
      <button type="button" class="font-medium text-base-content/70 hover:text-base-content" onclick={clearAll}>Clear all</button>
    </div>
  {/if}
</div>
